// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { Stack } from 'expo-router';
import { useMemo } from 'react';
import { Pressable, ScrollView, StyleSheet } from 'react-native';

import { Text, View } from '@/components/Themed';
import { useUser } from '@/app/user-context';
import { useEffectiveRoles } from '@/hooks/useEffectiveRoles';
import { useI18n } from '@/i18n';
import { getApiBaseUrl } from '@/services/apiBaseUrl';
import appColors from '@/theme/colors';
import {
  useBatchFleet,
  type FleetActivityItem,
  type FleetWorker,
} from '@/features/fleet/useBatchFleet';

/**
 * The batch-worker fleet, in one place: which off-cloud servers (services/batch-worker) have
 * reported in and how recently, how deep each job queue is, and what has run lately. A
 * developer's answer to "is anything on fire" and "did my release roll out" without SSHing
 * anywhere or reading CloudWatch — everything here already exists for the per-course and
 * per-job admin panels; this just puts it all on one screen instead of one course at a time.
 *
 * Developer-or-admin gated (see BFF GeodataEndpoints.ResolveDeveloperCaller): this is
 * infrastructure detail, not course data, so it does not require the course-admin role.
 */
export default function BatchFleetScreen() {
  const { t } = useI18n();
  const { user } = useUser();
  const { isDeveloper, isAdmin } = useEffectiveRoles(user);
  const canView = isDeveloper || isAdmin;
  const apiBaseUrl = useMemo(() => getApiBaseUrl(), []);
  const { state, refresh } = useBatchFleet(apiBaseUrl, user?.token, canView);

  return (
    <>
      <Stack.Screen options={{ title: t('batchFleetPage.title') }} />
      <ScrollView contentContainerStyle={styles.container}>
        <Text style={styles.title}>{t('batchFleetPage.title')}</Text>
        <Text style={styles.intro}>{t('batchFleetPage.intro')}</Text>

        {!canView ? (
          <Text style={styles.paragraph} testID="batch-fleet-forbidden">
            {t('batchFleetPage.forbidden')}
          </Text>
        ) : state.status === 'loading' ? (
          <Text style={styles.paragraph}>{t('batchFleetPage.loading')}</Text>
        ) : state.status === 'forbidden' ? (
          <Text style={styles.paragraph} testID="batch-fleet-forbidden">
            {t('batchFleetPage.forbidden')}
          </Text>
        ) : state.status === 'failed' ? (
          <>
            <Text style={styles.paragraph} testID="batch-fleet-failed">
              {t('batchFleetPage.failed')}
            </Text>
            <RefreshButton onPress={refresh} label={t('batchFleetPage.retry')} />
          </>
        ) : (
          <>
            <View style={styles.refreshRow}>
              <Text style={styles.updatedAt} testID="batch-fleet-updated-at">
                {t('batchFleetPage.updatedAt', { time: formatTime(state.loadedAt) })}
              </Text>
              <RefreshButton onPress={refresh} label={t('batchFleetPage.refresh')} />
            </View>

            <QueueRow queues={state.queues} t={t} />

            <Text style={styles.heading}>{t('batchFleetPage.workersHeading')}</Text>
            {state.workers.length === 0 ? (
              <Text style={styles.paragraph} testID="batch-fleet-no-workers">
                {t('batchFleetPage.noWorkers')}
              </Text>
            ) : (
              state.workers.map((worker) => <WorkerCard key={worker.workerId} worker={worker} t={t} />)
            )}

            <Text style={styles.heading}>{t('batchFleetPage.activityHeading')}</Text>
            {state.activity.length === 0 ? (
              <Text style={styles.paragraph} testID="batch-fleet-no-activity">
                {t('batchFleetPage.noActivity')}
              </Text>
            ) : (
              state.activity.map((item) => (
                <ActivityRow key={`${item.kind}-${item.id}`} item={item} t={t} />
              ))
            )}
          </>
        )}
      </ScrollView>
    </>
  );
}

function RefreshButton({ onPress, label }: { onPress: () => void; label: string }) {
  return (
    <Pressable style={styles.refreshButton} testID="batch-fleet-refresh" onPress={onPress}>
      <Text style={styles.refreshButtonText}>{label}</Text>
    </Pressable>
  );
}

function QueueRow({
  queues,
  t,
}: {
  queues: { courseAnalysisDispatched: number; flyoverQueued: number; mlQueued: number };
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const tiles: Array<{ testID: string; label: string; count: number }> = [
    {
      testID: 'batch-fleet-queue-analysis',
      label: t('batchFleetPage.queueCourseAnalysis'),
      count: queues.courseAnalysisDispatched,
    },
    {
      testID: 'batch-fleet-queue-flyover',
      label: t('batchFleetPage.queueFlyover'),
      count: queues.flyoverQueued,
    },
    { testID: 'batch-fleet-queue-ml', label: t('batchFleetPage.queueMl'), count: queues.mlQueued },
  ];

  return (
    <View style={styles.queueRow}>
      {tiles.map((tile) => (
        <View key={tile.testID} style={[styles.queueTile, tile.count > 0 && styles.queueTileBusy]} testID={tile.testID}>
          <Text style={styles.queueTileCount}>{tile.count}</Text>
          <Text style={styles.queueTileLabel}>{tile.label}</Text>
        </View>
      ))}
    </View>
  );
}

function WorkerCard({
  worker,
  t,
}: {
  worker: FleetWorker;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  return (
    <View style={styles.card} testID="batch-fleet-worker">
      <View style={styles.cardHeaderRow}>
        <View style={[styles.dot, worker.online ? styles.dotOnline : styles.dotOffline]} />
        <Text style={styles.cardTitle}>{worker.workerId}</Text>
        <Text style={styles.cardSubtitle}>{worker.host}</Text>
      </View>
      <Text style={styles.meta}>
        {worker.online
          ? t('batchFleetPage.online')
          : t('batchFleetPage.offlineSince', { time: formatAgo(worker.lastSeenAt, t) })}
      </Text>
      <Text style={styles.meta}>
        {t('batchFleetPage.version', { version: worker.version ?? t('batchFleetPage.versionUnknown') })}
      </Text>
      {worker.capabilities.length > 0 ? (
        <View style={styles.tagRow}>
          {worker.capabilities.map((capability) => (
            <View key={capability} style={styles.tag}>
              <Text style={styles.tagText}>{capability}</Text>
            </View>
          ))}
        </View>
      ) : null}
      <Text style={worker.currentJobId ? styles.metaBusy : styles.meta}>
        {worker.currentJobId ? t('batchFleetPage.busy') : t('batchFleetPage.idle')}
      </Text>
    </View>
  );
}

const ACTIVITY_TONE: Record<string, 'good' | 'warn' | 'bad'> = {
  succeeded: 'good',
  queued: 'warn',
  dispatched: 'warn',
  running: 'warn',
  partial: 'warn',
  failed: 'bad',
};

function ActivityRow({
  item,
  t,
}: {
  item: FleetActivityItem;
  t: (key: string, params?: Record<string, string | number>) => string;
}) {
  const tone = ACTIVITY_TONE[item.status] ?? 'warn';
  return (
    <View style={styles.card} testID="batch-fleet-activity-row">
      <View style={styles.cardHeaderRow}>
        <View style={[styles.badge, toneStyle(tone)]}>
          <Text style={styles.badgeText}>{item.status}</Text>
        </View>
        <Text style={styles.cardTitle}>{t(`batchFleetPage.kind.${item.kind}`)}</Text>
        <Text style={styles.cardSubtitle}>{item.summary ?? ''}</Text>
      </View>
      <Text style={styles.meta}>{formatTime(Date.parse(item.at))}</Text>
      {item.workerId ? (
        <Text style={styles.meta}>{t('batchFleetPage.byWorker', { id: item.workerId })}</Text>
      ) : null}
      {item.error ? (
        <Text style={styles.errorText} testID="batch-fleet-activity-error">
          {item.error}
        </Text>
      ) : null}
    </View>
  );
}

const toneStyle = (tone: 'good' | 'warn' | 'bad') =>
  tone === 'good' ? styles.badgeGood : tone === 'warn' ? styles.badgeWarn : styles.badgeBad;

const formatTime = (millis: number): string => {
  const parsed = new Date(millis);
  return Number.isNaN(parsed.getTime()) ? '' : parsed.toLocaleString();
};

const formatAgo = (iso: string, t: (key: string, params?: Record<string, string | number>) => string): string => {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return iso;
  const seconds = Math.max(0, Math.round((Date.now() - parsed) / 1000));
  if (seconds < 60) return t('batchFleetPage.secondsAgo', { count: seconds });
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return t('batchFleetPage.minutesAgo', { count: minutes });
  const hours = Math.round(minutes / 60);
  if (hours < 24) return t('batchFleetPage.hoursAgo', { count: hours });
  const days = Math.round(hours / 24);
  return t('batchFleetPage.daysAgo', { count: days });
};

const styles = StyleSheet.create({
  container: { padding: 20, gap: 4 },
  title: { fontSize: 26, fontWeight: '700', color: appColors.text.primary },
  intro: { color: appColors.text.secondary, fontSize: 13, marginBottom: 12, lineHeight: 18 },
  paragraph: { color: appColors.text.secondary, marginBottom: 12 },
  heading: { color: appColors.text.primary, fontWeight: '700', fontSize: 16, marginTop: 18, marginBottom: 8 },
  refreshRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  updatedAt: { color: appColors.text.muted, fontSize: 12 },
  refreshButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: appColors.accent.primary,
    alignSelf: 'flex-start',
  },
  refreshButtonText: { color: appColors.accent.primary, fontWeight: '700', fontSize: 12 },
  queueRow: { flexDirection: 'row', gap: 10, flexWrap: 'wrap', marginTop: 8 },
  queueTile: {
    flexGrow: 1,
    minWidth: 120,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    paddingVertical: 12,
    paddingHorizontal: 14,
    alignItems: 'center',
  },
  queueTileBusy: { borderColor: '#6B5A1E' },
  queueTileCount: { color: appColors.text.primary, fontSize: 22, fontWeight: '800' },
  queueTileLabel: { color: appColors.text.secondary, fontSize: 11, marginTop: 2, textAlign: 'center' },
  card: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: appColors.border.default,
    backgroundColor: appColors.background.panel,
    padding: 12,
    marginBottom: 10,
    gap: 4,
  },
  cardHeaderRow: { flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' },
  cardTitle: { color: appColors.text.primary, fontWeight: '700', fontSize: 14 },
  cardSubtitle: { color: appColors.text.secondary, fontSize: 12 },
  dot: { width: 9, height: 9, borderRadius: 5 },
  dotOnline: { backgroundColor: '#3FA65A' },
  dotOffline: { backgroundColor: '#8A8A78' },
  meta: { color: appColors.text.secondary, fontSize: 12 },
  metaBusy: { color: '#D7B65C', fontSize: 12, fontWeight: '600' },
  errorText: { color: '#E08A8A', fontSize: 12 },
  tagRow: { flexDirection: 'row', gap: 6, flexWrap: 'wrap', marginTop: 2 },
  tag: {
    paddingHorizontal: 8,
    paddingVertical: 2,
    borderRadius: 6,
    backgroundColor: appColors.background.app,
    borderWidth: 1,
    borderColor: appColors.border.default,
  },
  tagText: { color: appColors.text.secondary, fontSize: 10 },
  badge: { paddingHorizontal: 8, paddingVertical: 2, borderRadius: 6 },
  badgeGood: { backgroundColor: '#1F5B2E' },
  badgeWarn: { backgroundColor: '#6B5A1E' },
  badgeBad: { backgroundColor: '#7A2E2E' },
  badgeText: { color: appColors.text.primary, fontSize: 10, fontWeight: '800', textTransform: 'uppercase' },
});
